/**
 * A person Majik deals with — a client's point of contact, a billing inbox, a
 * cleaner, an inspector. One record per person, so a phone number lives in one
 * place (docs/majik/DATA-ARCHITECTURE-SPEC.md → `contacts`). Pure.
 */
import { z } from 'zod'
import { recordIdSchema } from '../shared/record-id.ts'

export const languages = ['english', 'french', 'spanish'] as const
export type Language = (typeof languages)[number]

export const languageLabels: Record<Language, string> = {
  english: 'English',
  french: 'French',
  spanish: 'Spanish',
}

export const contactMethodKinds = ['phone', 'email'] as const

/**
 * Another way to reach someone (docs/majik/OPEN-ITEMS.md 3.4): a work email,
 * an old mobile. Kept when it stops being used, so merging two records of the
 * same person never loses a number, and searching by an old number still finds
 * them (matching an incoming text to it comes with the texting connection).
 */
export const contactMethodSchema = z.object({
  kind: z.enum(contactMethodKinds),
  /** A phone as E.164 (+12125550100); an email lowercase. */
  value: z.string().trim().min(1).max(120),
  /** "Work", "Old mobile". */
  label: z.string().trim().max(40).default(''),
  /** Still in use. An old one is kept, never texted or emailed. */
  current: z.boolean().default(true),
})
export type ContactMethod = z.infer<typeof contactMethodSchema>

export const contactSchema = z.object({
  id: recordIdSchema,
  firstName: z.string().trim().min(1, 'Give them a name.').max(80),
  lastName: z.string().trim().max(80).default(''),
  /** Stored lowercase. Null when only a phone is known. */
  email: z.email().nullable().default(null),
  /** Mobile, E.164 (+12125550100). Null when only an email is known. */
  phone: z
    .string()
    .regex(/^\+\d{10,15}$/, 'Phone numbers are stored as +1 and ten digits.')
    .nullable()
    .default(null),
  /** Picks the language their messages are sent in. */
  language: z.enum(languages).default('english'),
  /** They texted STOP, or the carrier said so: never text them. */
  textsBlocked: z.boolean().default(false),
  /** Can be sent an inspection. */
  isInspector: z.boolean().default(false),
  /** Works in Majik's office — kept for staff broadcasts; not a cleaner (DATA-MODEL-V1 §4.2). */
  isStaff: z.boolean().default(false),
  /** Numbers and emails besides the main ones, current or old. */
  otherMethods: z.array(contactMethodSchema).max(10).default([]),
  createdAt: z.iso.datetime(),
  updatedAt: z.iso.datetime(),
  deletedAt: z.iso.datetime().nullable().default(null),
})
export type Contact = z.infer<typeof contactSchema>

export function contactName(contact: Pick<Contact, 'firstName' | 'lastName'>): string {
  return `${contact.firstName} ${contact.lastName}`.trim()
}

/**
 * A name that is really a phone number or an email: Monday had no name for
 * them, so the import kept what it had. Listed after everyone with a real
 * name (Gideon, Oct 28) — the same rule in both repositories' search.
 */
export function hasPlaceholderName(contact: Pick<Contact, 'firstName' | 'lastName'>): boolean {
  const name = contactName(contact)
  return /^[+(\d]/.test(name) || name.includes('@')
}

/** "AP" for Accounts Payable, "G" for Georgia. */
export function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((word) => word[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase()
}

/** "Maria Z." — first name and an initial, where a row has little room. */
export function shortName(name: string): string {
  const [first = '', second] = name.split(/\s+/)
  return second ? `${first} ${second[0]}.` : first
}

/** "(212) 555-0100" from "+12125550100"; anything else as stored. */
export function phoneText(phone: string | null): string {
  if (!phone) return ''
  const us = phone.match(/^\+1(\d{3})(\d{3})(\d{4})$/)
  return us ? `(${us[1]}) ${us[2]}-${us[3]}` : phone
}
